import React from 'react';
import { View, Image, Text } from 'react-native';
import { StyleProp, ViewStyle } from 'react-native';

interface UserAvatarProps {
  avatarUrl?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  size?: number;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * UserAvatar component that displays user avatar image or falls back to
 * initials with color-coded background (matching Avatar component design)
 */
export function UserAvatar({
  avatarUrl,
  name,
  firstName,
  lastName,
  size = 48,
  style,
  testID,
}: UserAvatarProps) {
  const [imageError, setImageError] = React.useState(false);

  // Material Design 500 colors for avatar backgrounds (matching Avatar component)
  const AVATAR_COLORS = [
    '#ef4444', // red-500
    '#f97316', // orange-500
    '#f59e0b', // amber-500
    '#84cc16', // lime-500
    '#10b981', // emerald-500
    '#06b6d4', // cyan-500
    '#3b82f6', // blue-500
    '#6366f1', // indigo-500
    '#8b5cf6', // violet-500
    '#d946ef', // fuchsia-500
    '#f43f5e', // rose-500
  ];

  const getAvatarColor = (identifier: string): string => {
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
    }
    return (
      AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] || AVATAR_COLORS[0]
    );
  };

  const getInitials = (
    name?: string | null,
    firstName?: string | null,
    lastName?: string | null
  ): string => {
    // Prioritize firstName + lastName if available
    if (firstName || lastName) {
      const first = firstName?.charAt(0) || '';
      const last = lastName?.charAt(0) || '';
      if (first && last) return (first + last).toUpperCase();
      if (first) return first.toUpperCase();
      if (last) return last.toUpperCase();
    }

    // Fall back to name parsing
    if (!name) return '?';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      // For single word, take first and last character (e.g., "Meshal" -> "ML")
      const word = words[0] || 'OP';
      if (word.length === 1) return word.toUpperCase();
      return (word.charAt(0) + word.charAt(word.length - 1)).toUpperCase();
    }
    const first = words[0]?.charAt(0) || '';
    const last = words[words.length - 1]?.charAt(0) || '';
    return (first + last).toUpperCase();
  };

  const fontSize = Math.floor(size * 0.4);
  const colorIdentifier = firstName || lastName || name || 'default';
  const backgroundColor = getAvatarColor(colorIdentifier);

  return (
    <View
      style={[
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor:
            avatarUrl && !imageError ? 'transparent' : backgroundColor,
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        },
        style,
      ]}
      testID={testID}
    >
      {avatarUrl && !imageError ? (
        <Image
          source={{ uri: avatarUrl }}
          style={{
            width: size,
            height: size,
            borderRadius: size / 2,
          }}
          resizeMode='cover'
          onError={() => setImageError(true)}
        />
      ) : (
        <Text
          style={{
            fontSize,
            fontWeight: '600',
            color: '#ffffff',
            textAlign: 'center',
          }}
        >
          {getInitials(name, firstName, lastName)}
        </Text>
      )}
    </View>
  );
}
